/**
 * Catalogue warm-up behind the brand field's combined "Brand · Model" rows (ENG-4831 B3).
 *
 * A model-name query in the brand field ("srg-a40" → "Sony · SRG-A40") can only match models
 * that are already cached, and the registry has no all-models endpoint
 * (`DeviceRegistryModelsList` takes one brand). So the brand field warms the cache brand by
 * brand — but only once the user starts typing (combined rows do not exist for an empty query),
 * best-guess brands first, off the shared registry mutex, and at most once per brand per
 * session (see `prefetchModels`). Rows then fill in progressively from whatever is cached, by
 * any path: the prefetch, the user's own `useModels`, the not-listed flow.
 *
 * Lives apart from `hooks.ts` on purpose: its readers (`Details`) are rendered by suites that
 * stub that module's exact export surface.
 */
import { MatchScore, normalize, scoreMatch } from '@studio/design-system/combobox';
import type { Brand } from '@studio/models/device';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { useEffect } from 'react';
import type { Collection } from '#~/api/device/registry/index.js';
import { useAuth0 } from '#~/auth/index.js';
import { useDepot } from '#~/depot/hooks.js';
import { prefetchModels } from '#~/device/registry/commands/index.js';
import type { Registry } from '#~/device/registry/types.js';

/**
 * Brands to warm for a brand-field query, best first: brands whose name the FIRST word of the
 * query names ("sony srg" → Sony before the rest; "s" → every S brand first), then the rest in
 * catalogue order. The generic "Other" brand is skipped — it never contributes combined rows and
 * the not-listed flow loads it itself when needed.
 *
 * @param query - Raw brand-field text (non-empty).
 * @param brands - Registry brands.
 * @returns Brand ids in prefetch order.
 */
export const orderBrandIdsForQuery = (query: string, brands: Collection<Brand>): Brand['id'][] => {
  const [firstWord = ''] = query.trim().split(/\s+/u);
  const listed = Object.values(brands.items).filter((brand) => brand.id !== brands.generic?.id);
  const likely = new Set(
    listed.filter((brand) => scoreMatch(firstWord, brand.name) >= MatchScore.wordPrefix)
  );
  const rest = listed.filter((brand) => !likely.has(brand));

  return [...likely, ...rest].map((brand) => brand.id);
};

/**
 * The registry model cache for combined "Brand · Model" rows, warmed as the user types.
 *
 * Returns the depot's `registry.models` slice itself — referentially stable between writes, so
 * the memoised `<Brand>` (compared on `models` identity) re-renders only when a brand lands —
 * and starts the catalogue prefetch the first time `query` becomes non-empty, re-prioritising
 * on later changes. Nothing is requested on mount or for an empty query.
 *
 * @param brands - Registry brands (from `useBrands`); nothing is warmed until they exist.
 * @param query - Current brand-field text (`<Brand onQueryChange>`).
 * @returns Models per brand, as cached so far.
 */
export const useCatalogueModels = (
  brands: T_Maybe<Collection<Brand>>,
  query: string
): Registry['models'] => {
  const models = useDepot((µ) => µ.registry.models);
  const { getAccessTokenSilently } = useAuth0();

  // Same emptiness rule as the combined rows: punctuation alone is nothing to search for.
  const typing = normalize(query).length > 0;

  // An effect, never the render body: a render must not issue requests (strict mode renders
  // twice, parents re-render freely). `prefetchModels` dedups per brand per session anyway.
  useEffect(() => {
    if (!typing || !brands) {
      return;
    }

    prefetchModels(orderBrandIdsForQuery(query, brands), getAccessTokenSilently);
  }, [brands, getAccessTokenSilently, query, typing]);

  return models;
};
